// The station window's **Windows & games** tab: how a network-started PC starts, and what staff can do about it — for a
// station linked to a boot PC, at a venue with Clavian Boot, for a member holding `boot:view`.
//
// The facts: its Windows image and the version its last start ran ("Latest", or "Update waiting · version 5 is ready"),
// where that choice comes from ("Set for this PC", "From its class Standard PC", "The venue's default"), its game disks
// with their drive letters and where they come from, its last start and how long it took, Secure Boot, its graphics and
// its motherboard. A PC that cannot start says why, and what to do, in a sentence; a PC whose motherboard is not the one
// its image was made on says so (an image works on the model it was made on).
//
// The actions, each confirmed in place under the buttons — the station window is already a dialog, and a dialog never
// opens a dialog:
//   - Restart onto the latest when free (`boot:manage`, only with an update waiting): the rollout's own restart, for this
//     PC alone — never during a session or with a booking in the next 30 minutes; the answer says why it was left alone;
//   - Reset this PC's changes (`boot:wipe`): its write-back thrown away, with a reason for the record;
//   - Change Windows using this PC (`boot:edit_image`): an edit boot of its image on this PC; not while someone plays;
//   - Choose Windows for this PC (`boot:manage`): its own image and games, before its class's, or back to its class's.
// Under a collapsed Advanced: where its changes are kept (its write-back override) and sharing its shader cache with its
// graphics family (`boot:manage`), and clearing shader caches (`boot:wipe`). These are the PCs tab's actions, which
// moved here when that tab was folded into the Floor.

import type { BootFloorPc, BootRolloutRestart, BootWritebackPolicy } from '@clavian/api-client';
import { useTranslation, useVenueFormatter } from '@clavian/i18n/react';
import { Button, Checkbox, RadioGroup, SegmentedControl, SelectField, TextField, useToast } from '@clavian/ui-web';
import { Link } from '@tanstack/react-router';
import { useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react';

import { problemDetail } from '../api/problems';
import { useNow } from '../floor/live';
import { useCurrentVenue } from '../shell/scope';
import { Facts, Notice, Section } from '../ui';
import { bootStripOf } from './floor-boot';
import { gpuFamilyLabel, motherboardLabel, motherboardMismatch } from './hardware';
import { useBootImages, usePromoteBootShaderSeed, useRestartIdleBootPcs, useStartBootEdit, useUpdateBootPc, useWipeBoot, type BootScope } from './queries';

import './station-boot.css';

export interface StationBootRights {
  readonly canManage: boolean;
  readonly canWipe: boolean;
  readonly canEditImage: boolean;
}

type Open = 'restart' | 'reset' | 'change' | 'choose' | 'seed' | 'clearShader' | null;

export function StationBoot({
  scope,
  pc,
  className,
  rights,
  busy,
}: {
  readonly scope: BootScope;
  readonly pc: BootFloorPc;
  /** The station's class, named where its Windows or games come from it. */
  readonly className?: string | undefined;
  readonly rights: StationBootRights;
  /** Someone is playing on the station: Windows is changed on an idle PC only. */
  readonly busy: boolean;
}): ReactNode {
  const { t } = useTranslation('console');
  const format = useVenueFormatter();
  const venue = useCurrentVenue();
  const [open, setOpen] = useState<Open>(null);
  const toggle = (next: Exclude<Open, null>) => setOpen((current) => (current === next ? null : next));
  const close = () => setOpen(null);
  const strip = bootStripOf(pc, t, useNow(30_000));
  const image = pc.image;

  const canRestart = rights.canManage && pc.state === 'update_waiting' && image !== null && !pc.editing;
  const canReset = rights.canWipe;
  const canChange = rights.canEditImage && image !== null && image.currentVersionNumber !== null && !pc.editing;
  const canChoose = rights.canManage && !pc.editing;
  const fromWords = (key: 'from' | 'gamesFrom', from: 'pc' | 'class' | 'venue' | undefined): string | null => {
    if (from === undefined) return null;
    if (from === 'class') return className === undefined ? t(`boot.floor.facts.${key}.classUnnamed`) : t(`boot.floor.facts.${key}.class`, { name: className });
    return t(`boot.floor.facts.${key}.${from}`);
  };
  // An image works on the motherboard model it was made on (ADR-0065 decision 8): both known and different, it is said.
  const boards = motherboardMismatch(pc.motherboard, pc.imageMotherboard);

  const windows =
    image === null ? (
      t('boot.floor.facts.noImage')
    ) : (
      <span className="console-boot-pc__windows">
        <span>
          {image.name}
          {image.versionNumber === null ? '' : ` · ${t('boot.floor.facts.version', { number: image.versionNumber })}`}
        </span>
        <span className="console-muted">
          {[
            image.latest ? t('boot.floor.facts.latest') : pc.state === 'update_waiting' && image.currentVersionNumber !== null ? t('boot.floor.facts.updateWaiting', { number: image.currentVersionNumber }) : null,
            fromWords('from', image.from) ?? (image.own ? t('boot.floor.facts.own') : null),
          ]
            .filter((part): part is string => part !== null)
            .join(' · ')}
        </span>
      </span>
    );
  const gameList =
    pc.gameDisks.length === 0
      ? t('boot.floor.facts.noGames')
      : pc.gameDisks.map((disk) => (disk.driveLetter === null ? t('boot.floor.facts.gameDiskNoLetter', { name: disk.name }) : t('boot.floor.facts.gameDisk', { name: disk.name, letter: disk.driveLetter }))).join(', ');
  const gamesFrom = fromWords('gamesFrom', pc.gameDisksFrom);
  const games =
    gamesFrom === null ? (
      gameList
    ) : (
      <span className="console-boot-pc__windows">
        <span>{gameList}</span>
        <span className="console-muted">{gamesFrom}</span>
      </span>
    );
  const lastStart =
    pc.lastStartAt === null
      ? t('boot.floor.facts.neverStarted')
      : pc.bootSeconds === null
        ? format.relative(pc.lastStartAt)
        : t('boot.floor.facts.lastStartTook', { when: format.relative(pc.lastStartAt), seconds: pc.bootSeconds });
  const unknown = t('boot.floor.facts.unknown');

  return (
    <div className="console-station-modal__stack console-boot-pc">
      {pc.state === 'cant_start' && pc.problem !== undefined ? (
        <Notice tone="danger" title={strip.reason ?? strip.words}>
          {pc.problem === 'other_motherboard' && boards !== null ? <p>{t('boot.floor.motherboard.mismatch', boards)}</p> : null}
          <p>{t(`boot.floor.fix.${pc.problem}`)}</p>
        </Notice>
      ) : pc.state === 'unknown' ? (
        // The boot server went quiet: no stale state is shown as if it were true.
        <Notice tone="warn" title={strip.words}>
          <p>{strip.reason}</p>
          <p>{t('boot.floor.unknownFix')}</p>
        </Notice>
      ) : boards !== null ? (
        <Notice tone="warn" title={t('boot.floor.motherboard.title')}>
          <p>{t('boot.floor.motherboard.mismatch', boards)}</p>
        </Notice>
      ) : null}
      {pc.gameDiskLetterBlocked != null ? (
        <Notice tone="warn" title={t('boot.floor.letterBlocked.title')}>
          <p>{t('boot.floor.letterBlocked.body', { letter: pc.gameDiskLetterBlocked })}</p>
          {rights.canManage ? (
            <p>
              <Link className="clav-button clav-button--secondary clav-button--sm" to="/v/$venueSlug/boot" params={{ venueSlug: venue.slug }} search={{ page: 'games' } as never}>
                {t('boot.floor.letterBlocked.change')}
              </Link>
            </p>
          ) : null}
        </Notice>
      ) : null}
      {pc.editing ? <Notice tone="info">{t('boot.floor.editingNotice')}</Notice> : null}

      <Section title={t('boot.floor.section')} titleAs="h3" actions={<span className={`console-boot-pc__state console-boot-pc__state--${strip.tone}`}>{strip.words}</span>}>
        <Facts
          columns={2}
          items={[
            { key: 'windows', label: t('boot.floor.facts.windows'), value: windows },
            { key: 'games', label: t('boot.floor.facts.games'), value: games },
            { key: 'last', label: t('boot.floor.facts.lastStart'), value: lastStart },
            { key: 'secure', label: t('boot.floor.facts.secureBoot'), value: pc.secureBoot === null ? unknown : pc.secureBoot ? t('boot.floor.facts.on') : t('boot.floor.facts.off') },
            { key: 'graphics', label: t('boot.floor.facts.graphics'), value: pc.gpuFamily === null ? unknown : gpuFamilyLabel(pc.gpuFamily, t) },
            { key: 'motherboard', label: t('boot.floor.facts.motherboard'), value: pc.motherboard == null ? unknown : motherboardLabel(pc.motherboard) },
            { key: 'changes', label: t('boot.floor.facts.changes'), value: pc.writebackLocation === null ? unknown : t(`boot.pcs.writebackWhere.${pc.writebackLocation}`) },
          ]}
        />
        {canRestart || canReset || canChange || canChoose ? (
          <div className="console-boot-pc__actions" role="group" aria-label={t('boot.floor.actions.label')}>
            {canRestart ? (
              <Button variant={open === 'restart' ? 'primary' : 'secondary'} size="sm" aria-expanded={open === 'restart'} onClick={() => toggle('restart')}>
                {t('boot.floor.actions.restart')}
              </Button>
            ) : null}
            {canReset ? (
              <Button variant="secondary" size="sm" aria-expanded={open === 'reset'} onClick={() => toggle('reset')}>
                {t('boot.floor.actions.reset')}
              </Button>
            ) : null}
            {canChange ? (
              <Button variant="secondary" size="sm" aria-expanded={open === 'change'} onClick={() => toggle('change')}>
                {t('boot.floor.actions.change')}
              </Button>
            ) : null}
            {canChoose ? (
              <Button variant="secondary" size="sm" aria-expanded={open === 'choose'} onClick={() => toggle('choose')}>
                {t('boot.floor.choose.open')}
              </Button>
            ) : null}
          </div>
        ) : null}
        {open === 'restart' && canRestart ? <RestartConfirm scope={scope} pc={pc} onDone={close} /> : null}
        {open === 'reset' && canReset ? <ResetForm scope={scope} pc={pc} onDone={close} /> : null}
        {open === 'change' && canChange ? <ChangeConfirm scope={scope} pc={pc} busy={busy} onDone={close} /> : null}
        {open === 'choose' && canChoose ? <ChooseForm scope={scope} pc={pc} className={className} onDone={close} /> : null}
      </Section>

      {rights.canManage || rights.canWipe ? (
        <details className="console-boot-pc__advanced">
          <summary>{t('boot.floor.advanced.summary')}</summary>
          <div className="console-boot-pc__advanced-body">
            {rights.canManage ? <WritebackForm scope={scope} pc={pc} /> : null}
            <div className="console-boot-pc__actions">
              {rights.canManage && pc.gpuFamily !== null ? (
                <Button variant="secondary" size="sm" aria-expanded={open === 'seed'} onClick={() => toggle('seed')}>
                  {t('boot.floor.advanced.seed')}
                </Button>
              ) : null}
              {rights.canWipe ? (
                <Button variant="secondary" size="sm" aria-expanded={open === 'clearShader'} onClick={() => toggle('clearShader')}>
                  {t('boot.floor.advanced.clearShader')}
                </Button>
              ) : null}
            </div>
            {open === 'seed' && pc.gpuFamily !== null ? <SeedConfirm scope={scope} pc={pc} family={pc.gpuFamily} onDone={close} /> : null}
            {open === 'clearShader' ? <ClearShaderForm scope={scope} pc={pc} onDone={close} /> : null}
          </div>
        </details>
      ) : null}
    </div>
  );
}

/** A confirm in place: its title and words, an error if the request failed, then Cancel and the action. Focus moves into it. */
function InPlace({
  title,
  children,
  fields,
  error,
  onCancel,
  onSubmit,
  confirm,
  tone = 'primary',
  pending,
  disabled = false,
}: {
  readonly title: string;
  readonly children?: ReactNode;
  /** Fields the confirm asks for (a reason), under its words. */
  readonly fields?: ReactNode;
  readonly error: string | null;
  readonly onCancel: () => void;
  readonly onSubmit: (event: FormEvent) => void;
  readonly confirm: string;
  readonly tone?: 'primary' | 'danger';
  readonly pending: boolean;
  readonly disabled?: boolean;
}): ReactNode {
  const { t } = useTranslation('console');
  const ref = useRef<HTMLFormElement>(null);
  useEffect(() => {
    ref.current?.querySelector<HTMLElement>('input, button')?.focus();
  }, []);
  return (
    <form ref={ref} className="console-boot-pc__confirm" aria-label={title} noValidate onSubmit={onSubmit}>
      <Notice tone="info" title={title}>
        {children}
      </Notice>
      {fields}
      {error === null ? null : (
        <Notice tone="danger" role="alert">
          {error}
        </Notice>
      )}
      <div className="console-boot-pc__confirm-actions">
        <Button variant="ghost" size="sm" onClick={onCancel}>
          {t('common.cancel')}
        </Button>
        <Button type="submit" variant={tone} size="sm" busy={pending} disabled={disabled}>
          {confirm}
        </Button>
      </div>
    </form>
  );
}

function RestartConfirm({ scope, pc, onDone }: { readonly scope: BootScope; readonly pc: BootFloorPc; readonly onDone: () => void }): ReactNode {
  const { t } = useTranslation('console');
  const { show } = useToast();
  const restart = useRestartIdleBootPcs(scope);
  const [error, setError] = useState<string | null>(null);
  const image = pc.image;
  if (image === null) return null;

  function submit(event: FormEvent): void {
    event.preventDefault();
    if (image === null) return;
    setError(null);
    restart.mutate(
      { imageId: image.id, bootPcIds: [pc.bootPcId] },
      {
        onSuccess: (result: BootRolloutRestart) => {
          const skipped = result.skipped.find((entry) => entry.bootPcId === pc.bootPcId);
          if (result.restarted.some((entry) => entry.bootPcId === pc.bootPcId)) show({ tone: 'ok', title: t('boot.floor.restart.restarted') });
          else show({ tone: 'neutral', title: skipped === undefined ? t('boot.floor.restart.nothing') : t(`boot.floor.restart.left.${skipped.reason}`) });
          onDone();
        },
        onError: (caught) => setError(problemDetail(caught) ?? t('boot.floor.restart.failed')),
      },
    );
  }

  return (
    <InPlace title={t('boot.floor.restart.title')} error={error} onCancel={onDone} onSubmit={submit} confirm={t('boot.floor.restart.confirm')} pending={restart.isPending}>
      <p>{t('boot.floor.restart.body', { number: image.currentVersionNumber ?? 0, name: image.name })}</p>
    </InPlace>
  );
}

function ResetForm({ scope, pc, onDone }: { readonly scope: BootScope; readonly pc: BootFloorPc; readonly onDone: () => void }): ReactNode {
  const { t } = useTranslation('console');
  const { show } = useToast();
  const wipe = useWipeBoot(scope);
  const [reason, setReason] = useState('');
  const [missing, setMissing] = useState<string | undefined>(undefined);
  const [error, setError] = useState<string | null>(null);

  function submit(event: FormEvent): void {
    event.preventDefault();
    setError(null);
    const why = reason.trim();
    if (why === '') {
      setMissing(t('boot.floor.reset.reasonRequired'));
      return;
    }
    setMissing(undefined);
    wipe.mutate(
      { kind: 'writeback', scope: 'pc', bootPcId: pc.bootPcId, reason: why },
      {
        onSuccess: () => {
          show({ tone: 'ok', title: t('boot.floor.reset.sent') });
          onDone();
        },
        onError: (caught) => setError(problemDetail(caught) ?? t('boot.floor.reset.failed')),
      },
    );
  }

  return (
    <InPlace title={t('boot.floor.reset.title')} error={error} onCancel={onDone} onSubmit={submit} confirm={t('boot.floor.reset.confirm')} tone="danger" pending={wipe.isPending}
      fields={<TextField label={t('boot.floor.reset.reason')} description={t('boot.floor.reset.reasonHint')} required autoComplete="off" value={reason} error={missing} onChange={(event) => setReason(event.target.value)} />}
    >
      <p>{t('boot.floor.reset.body')}</p>
    </InPlace>
  );
}

function ChangeConfirm({ scope, pc, busy, onDone }: { readonly scope: BootScope; readonly pc: BootFloorPc; readonly busy: boolean; readonly onDone: () => void }): ReactNode {
  const { t } = useTranslation('console');
  const { show } = useToast();
  const start = useStartBootEdit(scope);
  const [error, setError] = useState<string | null>(null);
  const image = pc.image;
  if (image === null) return null;

  function submit(event: FormEvent): void {
    event.preventDefault();
    if (image === null || busy) return;
    setError(null);
    start.mutate(
      { imageId: image.id, bootPcId: pc.bootPcId },
      {
        onSuccess: () => {
          show({ tone: 'ok', title: t('boot.floor.change.started') });
          onDone();
        },
        onError: (caught) => setError(problemDetail(caught) ?? t('boot.floor.change.failed')),
      },
    );
  }

  return (
    <InPlace title={t('boot.floor.change.title')} error={error} onCancel={onDone} onSubmit={submit} confirm={t('boot.floor.change.confirm')} pending={start.isPending} disabled={busy}>
      <p>{t('boot.floor.change.body', { name: image.name })}</p>
      <p>{busy ? t('boot.floor.change.busy') : t('boot.floor.change.maintenance')}</p>
    </InPlace>
  );
}

const INHERIT = 'inherit';

/**
 * Choose Windows for this PC: its own image (or back to its class's, else the venue default) and its own game disks (or
 * back to its class's, else its image's). Sent as the PC's own override (`PATCH …/boot/pcs/{id}`); it takes effect at its
 * next restart when nobody is playing, like any change of image.
 */
function ChooseForm({ scope, pc, className, onDone }: { readonly scope: BootScope; readonly pc: BootFloorPc; readonly className: string | undefined; readonly onDone: () => void }): ReactNode {
  const { t } = useTranslation('console');
  const { show } = useToast();
  const images = useBootImages(scope);
  const update = useUpdateBootPc(scope);
  const own = pc.ownImageId ?? null;
  const ownGames = pc.ownGameDiskIds ?? null;
  const [imageChoice, setImageChoice] = useState<string>(own ?? INHERIT);
  const [gamesMode, setGamesMode] = useState<'inherit' | 'own'>(ownGames === null ? 'inherit' : 'own');
  const [picked, setPicked] = useState<readonly string[]>(ownGames ?? pc.gameDisks.map((disk) => disk.id));
  const [gamesError, setGamesError] = useState<string | undefined>(undefined);
  const [error, setError] = useState<string | null>(null);
  const live = (images.data ?? []).filter((image) => image.archivedAt === null && image.currentVersionId !== null);
  const systems = live.filter((image) => image.kind === 'system');
  const disks = live.filter((image) => image.kind === 'game');
  // What the PC falls back to without its own: its class's image when that is where it comes from now, else the default.
  const inherited = pc.image !== null && pc.image.from !== 'pc' ? pc.image.name : (systems.find((image) => image.defaultForVenue)?.name ?? null);
  const inheritLabel =
    pc.image?.from === 'class' && className !== undefined
      ? t('boot.floor.choose.inheritClass', { name: inherited ?? className })
      : inherited === null
        ? t('boot.floor.choose.inheritNone')
        : t('boot.floor.choose.inheritVenue', { name: inherited });

  function submit(event: FormEvent): void {
    event.preventDefault();
    setError(null);
    if (gamesMode === 'own' && picked.length === 0) {
      setGamesError(t('boot.floor.choose.gamesRequired'));
      return;
    }
    setGamesError(undefined);
    update.mutate(
      { bootPcId: pc.bootPcId, body: { imageId: imageChoice === INHERIT ? null : imageChoice, gameDiskIds: gamesMode === 'inherit' ? null : [...picked] } },
      {
        onSuccess: () => {
          show({ tone: 'ok', title: t('boot.floor.choose.saved') });
          onDone();
        },
        onError: (caught) => setError(problemDetail(caught) ?? t('boot.floor.choose.failed')),
      },
    );
  }

  return (
    <InPlace title={t('boot.floor.choose.title')} error={error} onCancel={onDone} onSubmit={submit} confirm={t('boot.floor.choose.save')} pending={update.isPending}
      fields={
        <div className="console-boot-pc__choose">
          <SelectField
            label={t('boot.floor.choose.image')}
            value={imageChoice}
            onValueChange={setImageChoice}
            options={[{ value: INHERIT, label: inheritLabel }, ...systems.map((image) => ({ value: image.id, label: image.name }))]}
          />
          {disks.length === 0 ? null : (
            <>
              <RadioGroup
                label={t('boot.floor.choose.games')}
                value={gamesMode}
                onValueChange={(value) => setGamesMode(value === 'own' ? 'own' : 'inherit')}
                options={[
                  { value: 'inherit', label: pc.gameDisksFrom === 'class' ? t('boot.floor.choose.gamesInheritClass') : t('boot.floor.choose.gamesInheritImage') },
                  { value: 'own', label: t('boot.floor.choose.gamesOwn') },
                ]}
              />
              {gamesMode === 'own' ? (
                <fieldset className="console-boot-pc__disks">
                  <legend className="console-subtle">{t('boot.floor.choose.gamesPick')}</legend>
                  {disks.map((disk) => (
                    <Checkbox
                      key={disk.id}
                      label={disk.driveLetter === null ? disk.name : t('boot.floor.facts.gameDisk', { name: disk.name, letter: disk.driveLetter })}
                      checked={picked.includes(disk.id)}
                      onCheckedChange={(checked) => setPicked((current) => (checked === true ? [...current.filter((id) => id !== disk.id), disk.id] : current.filter((id) => id !== disk.id)))}
                    />
                  ))}
                  {gamesError === undefined ? null : (
                    <p className="console-field-error" role="alert">
                      {gamesError}
                    </p>
                  )}
                </fieldset>
              ) : null}
            </>
          )}
        </div>
      }
    >
      <p>{t('boot.floor.choose.body')}</p>
    </InPlace>
  );
}

function WritebackForm({ scope, pc }: { readonly scope: BootScope; readonly pc: BootFloorPc }): ReactNode {
  const { t } = useTranslation('console');
  const { show } = useToast();
  const update = useUpdateBootPc(scope);
  const [policy, setPolicy] = useState<string>(pc.writebackPolicy ?? INHERIT);
  const [error, setError] = useState<string | null>(null);
  const changed = policy !== (pc.writebackPolicy ?? INHERIT);

  function submit(event: FormEvent): void {
    event.preventDefault();
    setError(null);
    update.mutate(
      { bootPcId: pc.bootPcId, body: { writebackPolicy: policy === INHERIT ? null : (policy as BootWritebackPolicy) } },
      {
        onSuccess: () => show({ tone: 'ok', title: t('boot.floor.advanced.writebackSaved') }),
        onError: (caught) => setError(problemDetail(caught) ?? t('boot.floor.advanced.writebackFailed')),
      },
    );
  }

  return (
    <form className="console-boot-pc__writeback" noValidate aria-label={t('boot.floor.advanced.writeback')} onSubmit={submit}>
      <SelectField
        label={t('boot.floor.advanced.writeback')}
        description={t('boot.floor.advanced.writebackHint')}
        value={policy}
        onValueChange={setPolicy}
        options={[
          { value: INHERIT, label: t('boot.pcs.writebackDialog.inherit') },
          { value: 'auto', label: t('settings.option.boot__writebackPolicy.auto') },
          { value: 'forceLocal', label: t('settings.option.boot__writebackPolicy.forceLocal') },
          { value: 'forceServer', label: t('settings.option.boot__writebackPolicy.forceServer') },
        ]}
      />
      {error === null ? null : (
        <Notice tone="danger" role="alert">
          {error}
        </Notice>
      )}
      <div>
        <Button type="submit" variant="secondary" size="sm" busy={update.isPending} disabled={!changed}>
          {t('boot.floor.advanced.saveWriteback')}
        </Button>
      </div>
    </form>
  );
}

function SeedConfirm({ scope, pc, family, onDone }: { readonly scope: BootScope; readonly pc: BootFloorPc; readonly family: string; readonly onDone: () => void }): ReactNode {
  const { t } = useTranslation('console');
  const { show } = useToast();
  const promote = usePromoteBootShaderSeed(scope);
  const [error, setError] = useState<string | null>(null);
  const words = gpuFamilyLabel(family, t);

  function submit(event: FormEvent): void {
    event.preventDefault();
    setError(null);
    promote.mutate(pc.bootPcId, {
      onSuccess: () => {
        show({ tone: 'ok', title: t('boot.pcs.seed.sent') });
        onDone();
      },
      onError: (caught) => setError(problemDetail(caught) ?? t('boot.pcs.seed.failed')),
    });
  }

  return (
    <InPlace title={t('boot.pcs.seed.title', { family: words })} error={error} onCancel={onDone} onSubmit={submit} confirm={t('boot.floor.advanced.seedConfirm')} pending={promote.isPending}>
      <p>{t('boot.floor.advanced.seedHint', { family: words })}</p>
    </InPlace>
  );
}

type ShaderScope = 'pc' | 'family' | 'fleet';

function ClearShaderForm({ scope, pc, onDone }: { readonly scope: BootScope; readonly pc: BootFloorPc; readonly onDone: () => void }): ReactNode {
  const { t } = useTranslation('console');
  const { show } = useToast();
  const wipe = useWipeBoot(scope);
  const family = pc.gpuFamily;
  const scopes: ShaderScope[] = family === null ? ['pc', 'fleet'] : ['pc', 'family', 'fleet'];
  const [which, setWhich] = useState<ShaderScope>('pc');
  const [reason, setReason] = useState('');
  const [missing, setMissing] = useState<string | undefined>(undefined);
  const [error, setError] = useState<string | null>(null);

  function submit(event: FormEvent): void {
    event.preventDefault();
    setError(null);
    const why = reason.trim();
    if (why === '') {
      setMissing(t('boot.pcs.wipe.reasonRequired'));
      return;
    }
    setMissing(undefined);
    wipe.mutate(
      {
        kind: 'shaderCache',
        scope: which,
        reason: why,
        ...(which === 'pc' ? { bootPcId: pc.bootPcId } : {}),
        ...(which === 'family' && family !== null ? { gpuFamily: family } : {}),
      },
      {
        onSuccess: () => {
          show({ tone: 'ok', title: t('boot.pcs.wipe.sent.shaderCache') });
          onDone();
        },
        onError: (caught) => setError(problemDetail(caught) ?? t('boot.pcs.wipe.failed')),
      },
    );
  }

  return (
    <InPlace title={t('boot.pcs.wipe.title.shaderCache')} error={error} onCancel={onDone} onSubmit={submit} confirm={t('boot.pcs.wipe.confirm.shaderCache')} tone="danger" pending={wipe.isPending}
      fields={
        <>
          <SegmentedControl
            label={t('boot.pcs.wipe.scope')}
            value={which}
            onValueChange={setWhich}
            options={scopes.map((one) => ({ value: one, label: t(`boot.pcs.wipe.scopes.${one}`, { family: gpuFamilyLabel(family, t) }) }))}
          />
          <TextField label={t('boot.pcs.wipe.reason')} description={t('boot.pcs.wipe.reasonHint')} required autoComplete="off" value={reason} error={missing} onChange={(event) => setReason(event.target.value)} />
        </>
      }
    >
      <p>{t('boot.pcs.wipe.intro.shaderCache')}</p>
    </InPlace>
  );
}
